/* eslint-disable @typescript-eslint/no-unused-vars */
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { Observable } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { AgencyModel, Product, ThresholdConfig } from 'src/app/interfaces/backend-models.interface';
import { ObjID } from 'src/app/interfaces/object-id.interface';
import {
	RuntimeDataEntry,
	RuntimeActionStatus,
	RuntimeDataUpdate,
	RuntimeWorkflowStatus,
	RuntimeFilter,
} from 'src/app/interfaces/runtime-data.interface';
import { getRandomElement, randIntRange } from 'src/app/utils/general-functions/general-functions.utils';
import { getUUID } from 'src/app/utils/id-utils/id.utils';
import * as sidetrayData from 'src/assets/nodes.json';
import * as workflowsData from 'src/assets/workflows.json';
import * as workflowChartData from 'src/assets/workflow-execution-data.json';
import * as actionChartData from 'src/assets/action-activity-data.json';
import { WorkflowData, WorkflowModel } from 'src/app/interfaces/workflow.interface';
import { ViewModel, WorkspaceDisableRule, WorkspaceDisableTimerInfo } from 'src/app/interfaces/views.interface';
import { SystemHealth } from 'src/app/interfaces/system-health.interface';
import {
	runtimeActionsChronologically,
	runtimeEntriesReverseChronologically,
} from 'src/app/utils/sorters/sorters.utils';
import { AclUserModel } from 'src/app/interfaces/user.interface';
import { LatLonLocation, NodeType } from 'src/app/interfaces/node-models.interface';
import { AgreementsModel } from 'src/app/interfaces/privacy-model.interface';
import { OrcUserInfo } from 'src/app/interfaces/orc-user-info.interface';
import { ActivateOrchestrate, SelfRegistrationStatus } from 'src/app/interfaces/self-registration-models.interface';
import { environment as env } from 'src/environments/environment';
import {
	CountResponse,
	ReliabilityReportCount,
	ReliabilityReportTime,
	TriggerExecutionChartData,
} from 'src/app/interfaces/data-visualization.interface';
import { SHEventAction } from 'src/app/interfaces/sh-event.interface';

/** Increment these to force demo users to load updated configs */
const DEMO_NODES_VERSION = '13';
const DEMO_WORKFLOWS_VERSION = '4';
const DEMO_VIEWS_VERSION = '5';
const DEMO_USERS_VERSION = '1';
const DEMO_AGENCY_VERSION = '3';

// comment any 2 from below:
// The user has accepted no agreements ever
const ORC_USER_INFO = 'orc-user-info';

// The user has accepted agreements but some are outdated
// const ORC_USER_INFO = 'orc-user-info-2';

// The user has accepted agreements and its not expired
// const ORC_USER_INFO = 'orc-user-info-3';
// localStorage.removeItem('demo_orc_user');

/**
 * Service for simulating API calls when running WebApp locally without Manage API
 * Any functionality added here should also be added to api.service.ts
 */
@Injectable({
	providedIn: 'root',
})
export class ApiService {
	constructor(private http: HttpClient) {}

	/** Workflows GET */
	public getWorkflows(): Observable<WorkflowModel[]> {
		const demoWorkflowsVersion = localStorage.getItem('demo_workflows_version');
		const response: WorkflowModel[] = JSON.parse(localStorage.getItem('demo_workflows'));
		if (response && demoWorkflowsVersion === DEMO_WORKFLOWS_VERSION) {
			return of(response).pipe(delay(250));
		} else {
			// Add WorkflowModel to cache so we have something to update on-save
			this.http.get<WorkflowModel[]>('../../../assets/workflows.json').subscribe(jsonWorkflows => {
				localStorage.setItem('demo_workflows', JSON.stringify(jsonWorkflows));
				localStorage.setItem('demo_workflows_version', DEMO_WORKFLOWS_VERSION);
			});
		}
		// Load workspace from JSON instead of from localStorage
		return this.http.get<WorkflowModel[]>('../../../assets/workflows.json').pipe(delay(250));
	}

	/** Sidetray GET */
	public getSidetray(): Observable<Product[]> {
		const demoNodesVersion = localStorage.getItem('demo_nodes_version');
		const nodes = localStorage.getItem('demo_nodes');
		// Load from cache if it exists and is up to date
		if (JSON.parse(nodes)?.length && demoNodesVersion === DEMO_NODES_VERSION) {
			return of(JSON.parse(nodes)).pipe(delay(250));
		} else {
			this.http.get<Product[]>('../../../assets/nodes.json').subscribe(jsonNodes => {
				localStorage.setItem('demo_nodes', JSON.stringify(jsonNodes));
				localStorage.setItem('demo_nodes_version', DEMO_NODES_VERSION);
			});
		}
		return this.http.get<Product[]>('../../../assets/nodes.json').pipe(delay(250));
	}

	/** Initialize GET */
	public getInitialize(): Observable<{ initialized: boolean }> {
		return of({ initialized: true }).pipe(delay(250));
	}

	/** Release Notes GET */
	public getReleaseNotes(fileName: string): Observable<string> {
		return this.http.get(`./assets/release-notes/${fileName}`, { responseType: 'text' }).pipe(delay(500));
	}

	// eslint-disable-next-line @typescript-eslint/member-ordering
	private inProgRuntimeEntry: RuntimeDataEntry; // Used to simulate In Progress -> Completed workflows via SignalR
	/**
	 * Returns a list of randomly generated runtime entries
	 * Up to 5 pages of 50 entries each are loaded
	 * If runtimeEntryId is provided, only one entry is returned
	 * and it will alternate between In Progress and Completed states
	 **/
	public getRuntimeHistory(filter: RuntimeFilter, ct?: string): Observable<RuntimeDataUpdate> {
		const MAX_LOAD_MORE = 5;
		const runtimeData: RuntimeDataEntry[] = [];

		// Load list of all demo workflows
		let workflows: WorkflowModel[] = JSON.parse(localStorage.getItem('demo_workflows'));
		if (!workflows) {
			// Use default list if cached data is unavailable
			workflows = (workflowsData as any).default as WorkflowModel[];
		}

		// Load list of all demo sidetray nodes
		let products: Product[] = JSON.parse(localStorage.getItem('demo_nodes'));
		if (
			!products ||
			!products.map(x => !!x.triggers.length).includes(true) ||
			!products.map(x => !!x.actions.length).includes(true)
		) {
			// Use default list if cached data is unavailable
			products = (sidetrayData as any).default as Product[];
		}
		const triggers: string[] = [];
		const actions: string[] = [];

		// Populate list of triggers and actions
		for (const product of products) {
			// Load triggers
			for (const trigger of product.triggers || []) {
				triggers.push(trigger.friendlyName);
			}
			// Load actions
			for (const action of product.actions || []) {
				actions.push(action.friendlyName);
			}
		}

		// First load's time range is from one week ago to present, then every following load is another week prior
		const weekEnd: number = Date.now() - (parseInt(ct) || 0) * 604800000;
		const weekStart: number = weekEnd - 604800000;

		// Only return one item if runtimeEntryId is provided
		let numResults = 50;
		if (filter?.runtimeEntryId) {
			numResults = 1;
		}

		// Create entries
		for (let i = 0; i < numResults; i++) {
			// Every other entry with single runtimeEntryId is "In Progress"
			const inProg: boolean = !this.inProgRuntimeEntry && !!filter?.runtimeEntryId;
			// 10% (at random) Suppressed, rest Completed
			const suppressed: boolean = (i + 1) % 10 === 0;

			// Generate random timestamp between 5 seconds ago and a week ago
			const randomEpochTime = randIntRange(weekStart, weekEnd - 5000);

			// Create table entry with no actions
			const timestamp = inProg
				? new Date(Date.now() - randIntRange(i * 1000 + 5000, i * 1000 + 6000)).toISOString()
				: new Date(randomEpochTime).toISOString();

			const workflow: WorkflowData = getRandomElement(workflows);

			let entry: RuntimeDataEntry;
			if (this.inProgRuntimeEntry && this.inProgRuntimeEntry.id === filter?.runtimeEntryId) {
				// If there is an In Progress workflow, complete it
				entry = {
					...this.inProgRuntimeEntry,
					status: RuntimeWorkflowStatus.completed,
					actionList: [],
				};
				this.inProgRuntimeEntry = null;
			} else {
				entry = {
					id: filter?.runtimeEntryId ?? getUUID(),
					scheduleTimestamp: timestamp,
					workflowName: workflow
						? workflow.name
						: $localize`Demo workflow ${getRandomElement('ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''))}`,
					workflowId: workflow ? workflow.id : undefined,
					workspaceId: workflow ? workflow.workspaceId : null,
					triggerName: getRandomElement(triggers),
					status: inProg
						? RuntimeWorkflowStatus.scheduled
						: suppressed
							? RuntimeWorkflowStatus.suppressed
							: RuntimeWorkflowStatus.completed,
					actionList: [],
				};
				if (filter?.runtimeEntryId) {
					this.inProgRuntimeEntry = entry;
				}
			}

			// Add 1 to 5 actions for each table entry
			const numActions = randIntRange(1, 5);
			for (let j = 0; j < numActions; j++) {
				if (inProg || suppressed) {
					continue;
				}
				entry.actionList.push({
					lastUpdatedAt: new Date(new Date(entry.scheduleTimestamp).getTime() + randIntRange(0, 10000)).toISOString(),
					actionName: getRandomElement(actions),
					status: getRandomElement([RuntimeActionStatus.succeeded, RuntimeActionStatus.failed], [16, 1]),
				});
				// Add failure reason to (some) failed actions
				entry.actionList[j].statusDetails =
					entry.actionList[j].status === RuntimeActionStatus.failed
						? getRandomElement(
								['Timed out', 'Could not locate device', 'Failed to communicate with product', undefined],
								[2, 2, 1, 4]
							)
						: undefined;
			}
			// Sort actions
			entry.actionList.sort(runtimeActionsChronologically);
			runtimeData.push(entry);
		}
		return of({
			// Increment ct. Once ct reaches 5, we stop loading more content
			ct: !ct || parseInt(ct) < MAX_LOAD_MORE ? ((parseInt(ct) || 0) + 1).toString() : null,
			runtimeData: runtimeData.sort(runtimeEntriesReverseChronologically),
		}).pipe(delay(500));
	}

	public getAgency(): Observable<AgencyModel> {
		// Removed cached agency when version is changed
		if (localStorage.getItem('demo_agency_version') !== DEMO_AGENCY_VERSION) {
			localStorage.removeItem('demo_agency');
		}

		if (localStorage.getItem('demo_agency')) {
			return of(JSON.parse(localStorage.getItem('demo_agency'))).pipe(delay(100));
		} else {
			this.http.get<AgencyModel>('../../../assets/agency.json').subscribe(agency => {
				localStorage.setItem('demo_agency', JSON.stringify(agency));
				localStorage.setItem('demo_agency_version', DEMO_AGENCY_VERSION);
			});
		}
		return this.http.get<AgencyModel>('../../../assets/agency.json').pipe(delay(100));
	}

	/** Rename Node PUT */
	public putRenameNode(id: ObjID, type: NodeType, name: string) {
		// Update localStorage with new FriendlyName
		const products: Product[] = JSON.parse(localStorage.getItem('demo_nodes'));
		for (const product of products) {
			// Search through nodes and rename the one with the matching vertexId
			const srcList = type === NodeType.outputOnly ? product.triggers : product.actions;
			for (const node of srcList) {
				if (node.id === id) {
					node.friendlyName = name;
					localStorage.setItem('demo_nodes', JSON.stringify(products));
					return of(true).pipe(delay(150));
				}
			}
		}
		return this.http.put('/error', null); // Node does not exist
	}

	public setTriggerIsCritical(triggerId: string, isCritical: boolean) {
		// Update localStorage with new isCritical state
		const products: Product[] = JSON.parse(localStorage.getItem('demo_nodes'));
		for (const product of products) {
			// Search through triggers and update the one with the matching vertexId
			for (const trigger of product.triggers) {
				if (trigger.id === triggerId) {
					trigger.isCritical = isCritical;
					localStorage.setItem('demo_nodes', JSON.stringify(products));
					return of(true).pipe(delay(150));
				}
			}
		}
		return this.http.put('/error', null); // Node does not exist
	}

	public setTriggerStaticLocation(triggerId: string, location: LatLonLocation) {
		// Update localStorage with new location
		const products: Product[] = JSON.parse(localStorage.getItem('demo_nodes'));
		for (const product of products) {
			// Search through triggers and update the one with the matching vertexId
			for (const trigger of product.triggers) {
				if (trigger.id === triggerId) {
					trigger.location = location;
					localStorage.setItem('demo_nodes', JSON.stringify(products));
					return of(true).pipe(delay(150));
				}
			}
		}
		return this.http.put('/error', null); // Node does not exist
	}

	public deleteTriggerStaticLocation(triggerId: string) {
		// Update localStorage with location removed
		const products: Product[] = JSON.parse(localStorage.getItem('demo_nodes'));
		for (const product of products) {
			// Search through triggers and update the one with the matching vertexId
			for (const trigger of product.triggers) {
				if (trigger.id === triggerId) {
					trigger.location = null;
					localStorage.setItem('demo_nodes', JSON.stringify(products));
					return of(true).pipe(delay(150));
				}
			}
		}
		return this.http.put('/error', null); // Node does not exist
	}

	public deleteWorkflow(workflow: WorkflowData) {
		const currentWorkflows: WorkflowModel[] = JSON.parse(localStorage.getItem('demo_workflows'));
		const newWorkflows = currentWorkflows.filter(x => x.id !== workflow.id);
		localStorage.setItem('demo_workflows', JSON.stringify(newWorkflows));
		return of(true).pipe(delay(500));
	}

	/** Workspace State PUT */
	public putWorkspaceState(state: boolean): Observable<AgencyModel> {
		// Update globalWorkflowEnabled on agency
		const agency: AgencyModel = JSON.parse(localStorage.getItem('demo_agency'));
		agency.globalWorkflowEnabled = state;
		localStorage.setItem('demo_agency', JSON.stringify(agency));
		return of(agency).pipe(delay(200));
	}

	/** Toggle workflow suppression */
	public toggleWorkflowSuppression(enabled: boolean): Observable<AgencyModel> {
		// Update suppressionDisabled on agency
		const agency: AgencyModel = JSON.parse(localStorage.getItem('demo_agency'));
		agency.suppressionDisabled = enabled;
		localStorage.setItem('demo_agency', JSON.stringify(agency));
		return of(agency).pipe(delay(200));
	}

	/** Save agency location */
	public saveAgencyLocation(latitude: number, longitude: number): Observable<AgencyModel> {
		const agency: AgencyModel = JSON.parse(localStorage.getItem('demo_agency'));
		agency.location = { latitude, longitude };
		localStorage.setItem('demo_agency', JSON.stringify(agency));
		return of(agency).pipe(delay(200));
	}

	/** Delete agency location */
	public deleteAgencyLocation(): Observable<AgencyModel> {
		const agency: AgencyModel = JSON.parse(localStorage.getItem('demo_agency'));
		agency.location = null;
		localStorage.setItem('demo_agency', JSON.stringify(agency));
		return of(agency).pipe(delay(200));
	}

	/** Toggle Aware Integration */
	public toggleAwareIntegration(enabled: boolean): Observable<AgencyModel> {
		const agency: AgencyModel = JSON.parse(localStorage.getItem('demo_agency'));
		agency.awareIntegrationEnabled = enabled;
		localStorage.setItem('demo_agency', JSON.stringify(agency));
		return of(agency).pipe(delay(200));
	}

	// Update product threshold config
	public putThresholdConfig(productId: ObjID, thresholdConfig: ThresholdConfig) {
		const demoNodesVersion = localStorage.getItem('demo_nodes_version');
		const nodesJson = localStorage.getItem('demo_nodes');
		let nodes: Product[] = JSON.parse(nodesJson);
		// Update cached value if it exists and is up to date
		if (nodes?.length && demoNodesVersion === DEMO_NODES_VERSION) {
			nodes = nodes.map(x => {
				// If productId matches, update the threshold information
				if (x.id === productId) {
					x.thresholdConfig = thresholdConfig;
				}
				return x;
			});
			localStorage.setItem('demo_nodes', JSON.stringify(nodes));
		}
		return of(true).pipe(delay(500));
	}

	/** Views GET */
	public getViews(): Observable<ViewModel[]> {
		const views: ViewModel[] = JSON.parse(localStorage.getItem('demo_views'));
		const demoViewsVersion = localStorage.getItem('demo_views_version');
		if (views && demoViewsVersion === DEMO_VIEWS_VERSION) {
			return of(views).pipe(delay(250));
		} else {
			this.http.get<ViewModel[]>('../../../assets/views.json').subscribe(v => {
				localStorage.setItem('demo_views', JSON.stringify(v));
				localStorage.setItem('demo_views_version', DEMO_VIEWS_VERSION);
			});
		}
		return this.http.get<ViewModel[]>('../../../assets/views.json').pipe(delay(250));
	}

	/** View GET */
	public getView(workspaceId: string): Observable<ViewModel> {
		const views: ViewModel[] = JSON.parse(localStorage.getItem('demo_views'));
		return of(views.find(view => view.id === workspaceId)).pipe(delay(200));
	}

	/** Views Update */
	public putView(newView: ViewModel) {
		const views: ViewModel[] = JSON.parse(localStorage.getItem('demo_views'));
		const viewIndex = views.findIndex(x => x.id === newView.id);
		if (viewIndex !== -1) {
			const existingView = views.splice(viewIndex, 1)[0];
			newView = {
				...newView,
				aclUsers: existingView.aclUsers,
				aclEnabled: existingView.aclEnabled,
			};
		} else {
			newView.aclUsers = [];
			newView.aclEnabled = true;
		}
		views.push(newView);
		localStorage.setItem('demo_views', JSON.stringify(views));
		return of(newView).pipe(delay(250));
	}

	/** Views DELETE */
	public deleteView(viewId: ObjID) {
		const views: ViewModel[] = JSON.parse(localStorage.getItem('demo_views'));
		localStorage.setItem('demo_views', JSON.stringify(views.filter(view => view.id !== viewId)));
		// Also delete all Workflows belonging to that Workspace
		const workflows: WorkflowModel[] = JSON.parse(localStorage.getItem('demo_workflows'));
		localStorage.setItem(
			'demo_workflows',
			JSON.stringify(workflows.filter(workflow => workflow.workspaceId !== viewId))
		);
		return of(true).pipe(delay(250));
	}

	/** Views Enable ACL for workspace (view) */
	public enableAcl(viewId: ObjID) {
		const views: ViewModel[] = JSON.parse(localStorage.getItem('demo_views'));
		const viewIndex = views.findIndex(x => x.id === viewId);
		views[viewIndex].aclEnabled = true;
		localStorage.setItem('demo_views', JSON.stringify(views));
		return of(views[viewIndex]).pipe(delay(250));
	}

	/** Views Disable ACL for workspace (view) */
	public disableAcl(viewId: ObjID) {
		const views: ViewModel[] = JSON.parse(localStorage.getItem('demo_views'));
		const viewIndex = views.findIndex(x => x.id === viewId);
		views[viewIndex].aclEnabled = false;
		localStorage.setItem('demo_views', JSON.stringify(views));
		return of(views[viewIndex]).pipe(delay(250));
	}

	/** Workflow changes PUT */
	public putWorkflow(workflow: WorkflowData): Observable<any> {
		const newWorkflow = {
			...workflow,
			_ts: Math.round(Date.now() / 1000),
			createdAt: Math.round(Date.now() / 1000),
			createdBy: 'orch_test_user@motorolasolutions.com',
			lastUpdatedAt: Math.round(Date.now() / 1000),
			lastUpdatedBy: 'orch_test_user@motorolasolutions.com',
		};
		const currentWorkflows: WorkflowModel[] = JSON.parse(localStorage.getItem('demo_workflows'));

		const existingWorkflowIndex = currentWorkflows.findIndex(wf => wf.id === workflow.id);
		if (existingWorkflowIndex > -1) {
			currentWorkflows[existingWorkflowIndex] = {
				...currentWorkflows[existingWorkflowIndex],
				...workflow,
				lastUpdatedAt: Math.round(Date.now() / 1000),
				lastUpdatedBy: 'orch_test_user@motorolasolutions.com',
			};
			localStorage.setItem('demo_workflows', JSON.stringify(currentWorkflows));
			return of(currentWorkflows[existingWorkflowIndex]).pipe(delay(500));
		}

		// If no wf id found, its a new wf
		currentWorkflows.push(newWorkflow);

		// Update cache with new configuration
		localStorage.setItem('demo_workflows', JSON.stringify(currentWorkflows));
		return of(newWorkflow).pipe(delay(500));
	}

	/** Workflow status PUT */
	public putWorkflowStatus(workflow: WorkflowData) {
		return this.putWorkflow(workflow);
	}

	public testWorkflow(
		workflowId: string,
		triggerId: string,
		correlationId: string,
		connectionId: string
	): Observable<RuntimeDataEntry> {
		const successMessage = $localize`Message successfully received`;
		const failureMessage = $localize`Device was offline`;
		const triggerName = $localize`Demo Trigger`;
		const actionName = $localize`Demo Action`;
		const workflowName = $localize`Demo Workflow`;
		const runtimeDataEntry: RuntimeDataEntry = {
			id: getUUID(),
			scheduleTimestamp: new Date().toUTCString(),
			workflowName, // Not used
			workflowId: null,
			workspaceId: null,
			triggerName,
			// Status of "test" allows us to skip straight to a completed simulation without the help of SignalRService
			status: RuntimeWorkflowStatus.test,
			actionList: Array.from('x'.repeat(3)).map((_, i) => ({
				lastUpdatedAt: new Date().toUTCString(),
				actionName: `${actionName} ${i + 1}`,
				status: i % 2 === 0 ? RuntimeActionStatus.succeeded : RuntimeActionStatus.failed,
				startTimestamp: new Date().toUTCString(),
				endTimestamp: new Date().toUTCString(),
				statusDetails: i % 2 === 0 ? successMessage : failureMessage,
			})),
		};
		return of(runtimeDataEntry).pipe(delay(1000));
	}

	/** System Health GET */
	public getSystemHealth(): Observable<SystemHealth[]> {
		const health: SystemHealth[] = JSON.parse(localStorage.getItem('demo_system_health'));
		if (health) {
			return of(health).pipe(delay(250));
		} else {
			this.http.get<SystemHealth[]>('../../../assets/system-health.json').subscribe(incidents => {
				localStorage.setItem('demo_system_health', JSON.stringify(incidents));
			});
		}
		return this.http.get<SystemHealth[]>('../../../assets/system-health.json').pipe(delay(250));
	}

	/** Users GET */
	public getUsers(): Observable<AclUserModel[]> {
		const users: AclUserModel[] = JSON.parse(localStorage.getItem('demo_users'));
		const demoUsersVersion = localStorage.getItem('demo_users_version');
		if (users && demoUsersVersion === DEMO_USERS_VERSION) {
			return of(users).pipe(delay(250));
		} else {
			this.http.get<AclUserModel[]>('../../../assets/users.json').subscribe(u => {
				localStorage.setItem('demo_users', JSON.stringify(u));
				localStorage.setItem('demo_users_version', DEMO_USERS_VERSION);
			});
		}
		return this.http.get<AclUserModel[]>('../../../assets/users.json').pipe(delay(250));
	}

	/** Add user to the workspace */
	public addUser(user: AclUserModel, workspaceId: ObjID): Observable<ViewModel> {
		const views: ViewModel[] = JSON.parse(localStorage.getItem('demo_views'));
		const viewIndex = views.findIndex(workspace => workspace.id === workspaceId);
		if (viewIndex === -1) return this.http.put<ViewModel>(`/error`, null);
		views[viewIndex].aclUsers.push({
			email: user.email,
			firstName: user.firstName,
			lastName: user.lastName,
		});
		localStorage.setItem('demo_views', JSON.stringify(views));
		return of(views[viewIndex]).pipe(delay(250));
	}

	/** Remove the user from the workspace */
	public removeUser(user: AclUserModel, workspaceId: ObjID) {
		const views: ViewModel[] = JSON.parse(localStorage.getItem('demo_views'));

		const viewIndex = views.findIndex(x => x.id === workspaceId);
		if (viewIndex === -1) return this.http.put<ViewModel>(`/error`, null);

		const userIndex = views[viewIndex].aclUsers.findIndex(v => v.email === user.email);
		if (userIndex === -1) return this.http.put<ViewModel>(`/error`, null);

		views[viewIndex].aclUsers.splice(userIndex, 1);

		localStorage.setItem('demo_views', JSON.stringify(views));
		return of(views[viewIndex]).pipe(delay(250));
	}

	public postErrorLog(error) {
		return of(true);
	}

	/** Post feedback to CMSO */
	public postFeedback(typeOfFeedback: string, title: string, details: string) {
		return of(true);
	}

	/** Checks if the user is registered with Orchestrate by sending a request to the ACS connector  */
	public getSelfRegStatus(token: string, source): Observable<SelfRegistrationStatus> {
		const selfRegStatus: SelfRegistrationStatus = {
			hasCentralStation: false,
			hasOrchestrate: false,
			isLinked: false,
		};
		return of(selfRegStatus);
	}

	public activateOrchestrate(token: string, activateOrchestrateBody: ActivateOrchestrate): Observable<any> {
		return of(true).pipe(delay(3000));
	}

	/** Patches user agreements based on the list of ids provided
	 * @param agreements Array of agreement IDs
	 * (eg.: msi_eula, msi_privacy_policy, pendo and twilio_sendgrid)
	 */
	public patchUserAgreementsOrc(agreements: string[]) {
		const user: OrcUserInfo = JSON.parse(localStorage.getItem('demo_orc_user'));
		const acceptedAt = new Date(Date.now()).toISOString();
		if (user) {
			agreements.forEach(a => {
				const index = user.acceptedAgreements.findIndex(ua => a === ua.agreementId);
				if (index > -1) {
					user.acceptedAgreements[index] = {
						...user.acceptedAgreements[index],
						acceptedAt,
					};
				} else {
					user.acceptedAgreements.push({ agreementId: a, acceptedAt });
				}
			});

			localStorage.setItem('demo_orc_user', JSON.stringify(user));
			return of(true).pipe(delay(300));
		}
	}

	/** Get the agreements from Orchestrate (if user is not in MFE) */
	public getAgreementsOrc(): Observable<AgreementsModel[]> {
		return this.http.get<AgreementsModel[]>('../../../assets/agreements.json').pipe(
			map(agreements => agreements.map(privacy => ({ ...privacy, checked: false }))),
			delay(100)
		);
	}

	/** Get the agreements from ACS connector (if user is in MFE) */
	public getAgreementsACS(token: string, source: string): Observable<AgreementsModel[]> {
		return this.http.get<AgreementsModel[]>('../../../assets/agreements.json').pipe(
			map(agreements => agreements.map(privacy => ({ ...privacy, checked: false }))),
			delay(100)
		);
	}

	/** Get the Orchestrate user info (CoreUser) (/get user) */
	public getOrcUserInfo(): Observable<OrcUserInfo> {
		if (localStorage.getItem('demo_orc_user')) {
			return of(JSON.parse(localStorage.getItem('demo_orc_user'))).pipe(delay(400));
		} else {
			this.http.get<OrcUserInfo>(`../../../assets/${ORC_USER_INFO}.json`).subscribe(userInfo => {
				localStorage.setItem('demo_orc_user', JSON.stringify(userInfo));
			});
			return this.http.get<OrcUserInfo>(`../../../assets/${ORC_USER_INFO}.json`).pipe(delay(100));
		}
	}

	/** Make request to CC Admin to change current agency */
	public switchAgency(agencyId: string, token: string) {
		const options = {
			headers: new HttpHeaders().append('Authorization', `Bearer ${token}`),
			params: new HttpParams(),
		};
		return this.http.patch(`${env.admin2ApiUrl}/current-agency?agency=${agencyId}`, null, options);
	}

	/** Get the WorkspaceDisableTimers for all provided workspaceIds */
	public getWorkspaceDisableTimers(workspaceIds: string[]): Observable<WorkspaceDisableTimerInfo[]> {
		return of([]).pipe(delay(250));
	}

	/** Get info about the presently running WorkspaceDisableTimer if one exists */
	public getWorkspaceDisableTimer(workspaceId: string) {
		return of(null).pipe(delay(250));
	}

	/** Edit the rule defining which trigger will prompt a workspace to be disabled for what duration */
	public editWorkspaceDisableRule(workspaceId: string, disableRule: WorkspaceDisableRule) {
		const views: ViewModel[] = JSON.parse(localStorage.getItem('demo_views'));
		const viewIndex = views.findIndex(x => x.id === workspaceId);
		views[viewIndex].disableRule = disableRule;
		localStorage.setItem('demo_views', JSON.stringify(views));
		return of(views[viewIndex]).pipe(delay(250));
	}

	/** Delete the rule defining which trigger will prompt a workspace to be disabled for what duration */
	public deleteWorkspaceDisableRule(workspaceId: string) {
		const views: ViewModel[] = JSON.parse(localStorage.getItem('demo_views'));
		const viewIndex = views.findIndex(x => x.id === workspaceId);
		views[viewIndex].disableRule = null;
		localStorage.setItem('demo_views', JSON.stringify(views));
		return of(views[viewIndex]).pipe(delay(250));
	}

	/** Cancel an existing WorkspaceDisableTimer if one exists */
	public cancelWorkspaceDisableTimer(workspaceId: string) {
		return of({ canceled: true }).pipe(delay(250));
	}

	/** Adjust an existing WorkspaceDisableTimer if one exists */
	public updateWorkspaceDisableTimer(workspaceId: string, minutes: number) {
		return of({ updated: true }).pipe(delay(250));
	}

	public getWorkflowExecutionData(filter: RuntimeFilter): Observable<CountResponse> {
		const chartData = (workflowChartData as any).default as CountResponse;
		const result: CountResponse = {
			...chartData,
			data: chartData.data
				.map(item => ({
					...item,
					buckets: item.buckets.map(bucket => ({
						...bucket,
						counts: bucket.counts.map(countObj => ({
							...countObj,
							count:
								countObj.type === 'COMPLETED'
									? Math.floor(Math.random() * 26) + 25 // Random value between 25 and 50
									: Math.floor(Math.random() * 26), // Random value between 0 and 25
						})),
					})),
				}))
				.filter(x => filter.workflowIds.includes(x.id)),
		};
		return of(result).pipe(delay(250));
	}

	/** Get Action Activity Chart Data*/
	public getActionActivityData(filter: RuntimeFilter): Observable<CountResponse> {
		const chartData = (actionChartData as any).default as CountResponse;
		const result: CountResponse = {
			...chartData,
			data: chartData.data.map(item => ({
				...item,
				buckets: item.buckets.map(bucket => ({
					...bucket,
					counts: bucket.counts.map(countObj => ({
						...countObj,
						count: Math.floor(Math.random() * 51), // Random value between 0 and 50
					})),
				})),
			})),
		};
		return of(result).pipe(delay(250));
	}

	/** Get Incident count data for bar chart */
	public getIncidentCountData(productIds: string[], filter: RuntimeFilter): Observable<ReliabilityReportCount> {
		return this.http.get<ReliabilityReportCount>(`../../../assets/incident-count-data.json`).pipe(delay(100));
	}

	/** Get Incident times data for doughnut chart */
	public getIncidentTimeData(productIds: string[], filter: RuntimeFilter): Observable<ReliabilityReportTime> {
		return this.http.get<ReliabilityReportTime>(`../../../assets/incident-time-data.json`).pipe(delay(100));
	}

	/** Get Trigger Execution Chart Data*/
	public getTriggerExecutionData(triggerIds: string[], filter: RuntimeFilter): Observable<TriggerExecutionChartData> {
		return this.http.get<TriggerExecutionChartData>(`../../../assets/trigger-execution-data.json`).pipe(delay(100));
	}

	public takeAction(actions: SHEventAction[]): Observable<any> {
		actions.forEach(action => {
			action.actionInstanceId = getUUID();
		});
		return of(actions).pipe(delay(500));
	}
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
